import React, { useState, useEffect } from 'react';
import {
  Modal, Button, Form, DatePicker, Select, Input, List, Tag, Popconfirm,
  Alert, Tooltip, Radio, message,
} from 'antd';
import { BellOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs, { fmtCN } from '../utils/time';
import { alarmApi, authApi } from '../services/api';

// 当前账号是否被允许企微推送（管理员在后台开启开关）：
// 整页只查一次 /auth/me，所有铃铛共享结果；未允许 → 列表完全不显示铃铛
// 注意：axios 响应拦截器已解包 response.data，此处拿到的就是用户对象本身
let _pushAllowedPromise = null;
function fetchPushAllowed() {
  if (!_pushAllowedPromise) {
    _pushAllowedPromise = authApi.getMe()
      .then((me) => !!(me && me.wecom_push_enabled === true))
      .catch(() => false);
  }
  return _pushAllowedPromise;
}

// 重复规则预设（cron 一律按北京时间解释，后端存储 UTC）
const REPEAT_OPTS = [
  { value: '0 9 * * 1-5', label: '每个工作日 09:00（北京时间）' },
  { value: '0 9 * * *', label: '每天 09:00（北京时间）' },
  { value: '0 9 * * 1', label: '每周一 09:00（北京时间）' },
  { value: 'custom', label: '自定义 cron 表达式' },
];

/**
 * 工单提醒闹钟铃铛（三大工单模块列表共用）。
 * Props:
 *   orderType: 'order' | 'appeal' | 'claim'
 *   orderId:   工单ID
 *   active:    当前用户对该工单是否已有生效闹钟（控制铃铛高亮）
 *   onChanged: 闹钟增删后回调（父组件刷新 mine 列表）
 */
export default function OrderAlarmBell({ orderType, orderId, active, onChanged }) {
  const [open, setOpen] = useState(false);
  const [alarms, setAlarms] = useState([]);
  const [pushReady, setPushReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('once'); // once=单次 repeat=重复
  const [onceTime, setOnceTime] = useState(null);
  const [repeat, setRepeat] = useState('0 9 * * 1-5');
  const [customCron, setCustomCron] = useState('');
  const [note, setNote] = useState('');
  const [pushAllowed, setPushAllowed] = useState(null); // null=查询中

  useEffect(() => { fetchPushAllowed().then(setPushAllowed); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const d = await alarmApi.list(orderType, orderId);
      setAlarms(d.items || []);
      setPushReady(!!d.push_ready);
    } catch (e) {
      /* 拦截器已提示 */
    } finally {
      setLoading(false);
    }
  };

  const openModal = (e) => {
    e.stopPropagation(); // 防止触发行点击等其他事件
    setOpen(true);
    load();
  };

  const submit = async () => {
    const payload = { order_type: orderType, order_id: orderId, note: note.trim() };
    if (mode === 'once') {
      if (!onceTime) {
        message.error('请选择提醒时间');
        return;
      }
      payload.remind_at = onceTime.format('YYYY-MM-DD HH:mm');
    } else {
      const cron = repeat === 'custom' ? customCron.trim() : repeat;
      if (!cron) {
        message.error('请输入 cron 表达式');
        return;
      }
      payload.cron_expr = cron;
    }
    setSaving(true);
    try {
      await alarmApi.create(payload);
      message.success('闹钟已设置，到点将通过企微提醒您');
      setOnceTime(null);
      setNote('');
      setCustomCron('');
      await load();
      onChanged && onChanged();
    } catch (e) {
      /* 拦截器已提示 */
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (id) => {
    try {
      await alarmApi.cancel(id);
      message.success('闹钟已取消');
      await load();
      onChanged && onChanged();
    } catch (e) {
      /* 拦截器已提示 */
    }
  };

  // 未被管理员允许企微推送的账号：列表里完全不渲染铃铛（查询中也不显示，避免闪烁）
  if (pushAllowed !== true) return null;

  return (
    <>
      <Tooltip title={active ? '已设提醒，点击查看/新增' : '设置企微提醒闹钟'}>
        <BellOutlined
          onClick={openModal}
          style={{
            cursor: 'pointer',
            fontSize: 15,
            fontWeight: 'bold',
            color: active ? '#cf1322' : '#ff4d4f',
            filter: 'drop-shadow(0 0 2px rgba(255, 77, 79, 0.55))',
          }}
        />
      </Tooltip>
      <Modal
        title="工单提醒闹钟（企微推送）"
        open={open}
        onOk={submit}
        onCancel={() => setOpen(false)}
        okText="设置闹钟"
        cancelText="关闭"
        confirmLoading={saving}
        width={540}
      >
        {!pushReady && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="您的账号尚未开启企微推送"
            description="闹钟可以先设置，但到点不会发送通知。请联系管理员在后台「前端账号管理」为您配置 Webhook 并开启企微推送。"
          />
        )}
        {alarms.length > 0 && (
          <List
            size="small"
            loading={loading}
            dataSource={alarms}
            style={{ marginBottom: 16, border: '1px solid #f0f0f0', borderRadius: 6, maxHeight: 180, overflowY: 'auto' }}
            renderItem={(a) => (
              <List.Item
                actions={[
                  <Popconfirm
                    key="del"
                    title="取消这个闹钟？"
                    onConfirm={() => cancel(a.id)}
                    okText="取消闹钟"
                    cancelText="再想想"
                  >
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>,
                ]}
              >
                <div style={{ fontSize: 13 }}>
                  <Tag color={a.cron_expr ? 'geekblue' : 'green'}>{a.cron_expr ? '重复' : '单次'}</Tag>
                  下次提醒：{fmtCN(a.next_run_at)}（北京）
                  {a.cron_expr && (
                    <span style={{ color: '#888', marginLeft: 8 }}>规则 {a.cron_expr}</span>
                  )}
                  {a.note && <div style={{ color: '#888', fontSize: 12 }}>备注：{a.note}</div>}
                </div>
              </List.Item>
            )}
          />
        )}
        <div style={{ marginBottom: 12 }}>
          <Radio.Group
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            options={[
              { value: 'once', label: '单次提醒' },
              { value: 'repeat', label: '重复提醒' },
            ]}
            optionType="button"
            buttonStyle="solid"
            size="small"
          />
        </div>
        {mode === 'once' ? (
          <Form.Item label="提醒时间（北京时间）" style={{ marginBottom: 8 }}>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              value={onceTime}
              onChange={setOnceTime}
              style={{ width: '100%' }}
              placeholder="选择日期和时间"
              disabledDate={(d) => d && d.isBefore(dayjs().startOf('day'))}
            />
          </Form.Item>
        ) : (
          <Form.Item label="重复规则（北京时间）" style={{ marginBottom: 8 }}>
            <Select value={repeat} onChange={setRepeat} options={REPEAT_OPTS} style={{ width: '100%' }} />
            {repeat === 'custom' && (
              <Input
                style={{ marginTop: 8 }}
                placeholder="cron 五段：分 时 日 月 周，如 0 9 * * 1-5 表示工作日9点"
                value={customCron}
                onChange={(e) => setCustomCron(e.target.value)}
                maxLength={100}
              />
            )}
          </Form.Item>
        )}
        <Form.Item label="备注（可选）" style={{ marginBottom: 0 }}>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={100}
            placeholder="例如：提醒客户确认提货时间"
          />
        </Form.Item>
      </Modal>
    </>
  );
}
